<template>
  <div>
    <!-- Segment selector and active segments list -->
    <div class="field-wrapper">
      <label class="block mb-2">
        {{ t(segmentLabel) }}
      </label>
      <Dropdown
        v-model="selectedValue"
        :options="options"
        class="unit-input select-segment"
        option-label="label"
        :placeholder="t(placeholderSelectSegments)"
        @update:model-value="loadSegment"
      />
    </div>

    <!-- Active segments list -->
    <div
      v-if="hasActiveSegment()"
      class="field-wrapper mt-3"
    >
      <label class="block mb-2">
        {{ t('automatedScenarios.fields.activeSegments') }}
      </label>
      <div
        v-for="(current) in activeSegments"
        :key="current"
        class="segment-item"
      >
        <div class="flex align-items-center">
          <div class="flex-grow-1" style="font-size: 14px;">
            {{ t(current.label) }}
          </div>
          <div class="flex gap-1">
            <Button
              v-if="current.show"
              v-tooltip="{ value: showTooltip(current.name, current.number), class: 'segment-summary' }"
              icon="far fa-eye"
              class="p-segment-icon p-button-rounded p-button-text p-button-info"
            />
            <Button
              v-if="current.show"
              icon="far fa-pencil"
              class="p-segment-icon p-button-rounded p-button-text p-button-success"
              @click="() => editSegment(current.name, current.number)"
            />
            <Button
              icon="far fa-times"
              class="p-segment-icon p-button-rounded p-button-text p-button-danger"
              @click="() => handleRemoveSegment(current.name, current.number)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for segment configuration -->
    <Dialog
      v-model:visible="showModal"
      :header="selectedSegment?.option?.label ? t(selectedSegment.option.label) : t('automatedScenarios.fields.configureSegment')"
      :modal="true"
      :closable="true"
      :draggable="false"
      class="segment-config-modal"
      :style="{ width: '450px' }"
      @hide="onModalHide"
    >
      <div class="segment-config-content">
        <div
          v-for="(field, index) in editableFields"
          :key="index"
          class="field-wrapper mb-3"
        >
          <component
            :is="resolveComponent(field.component)"
            v-if="!('show' in field) || (field.show && selectedSegment && field.show(selectedSegment.fields))"
            v-model="field.data"
            v-bind="field.props"
            :segment-id="selectedSegment?.id"
            :new-list-id="newListId"
          />
          <FieldErrors
            :key="componentFieldErrorsKey"
            :errors="formValidation"
            :field="field.id"
            :silent="false"
          />
        </div>
      </div>

      <template #footer>
        <div class="flex justify-content-end gap-2">
          <Button
            :label="t('automatedScenarios.cancel')"
            icon="fa-regular fa-xmark"
            class="p-button-text"
            @click="resetAll"
          />
          <Button
            :label="t('automatedScenarios.save')"
            icon="fa-regular fa-floppy-disk"
            class="p-button-primary"
            @click="handleSaveSegment"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, inject, PropType, Ref, ref, SetupContext, toRef, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Tooltip from 'primevue/tooltip';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import Dialog from 'primevue/dialog';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import tooltip from '@/components/automated-scenarios/forms/filtres/segments/tooltips';
import {
  ItemList,
  NumberRange,
  ButtonGroup,
  InputList,
  MultiDropdown,
  EditAutocomplete,
  InputNumber,
  PeriodSelector,
  Input,
  Location,
} from '@/components/flows/operators/shared';
import type { FilterField, FilterSegment } from '@/types/flow-types';
import { ActiveSegment, getAvailableSegmentsForFlow, FlowDependencyContext } from '@/composables/flows/useFlowSegments';
import type { OperatorInsertContext } from '@/components/flows/operators/registry';
import { useStore } from '@/store';
import { asInt, showToastError } from '@/helpers';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { TypeCampaignEnum } from '@/types';

import { List } from '@/composables/GraphQL';
import { UserState } from '@/composables/User';
import { myListsState } from '@/composables/mes-listes/ListsManage';
import { OperatorType, ShopsLists } from '@/types/generated-types/graphql';

export default defineComponent({
  name: 'FilterSegmentForm',

  components: {
    SpmButton,
    FieldErrors,
    BaseDropdown,
    Button,
    Dialog,
    ItemList,
    NumberRange,
    ButtonGroup,
    InputNumber,
    Input,
    Location,
    PeriodSelector,
    InputList,
    Dropdown,
    MultiDropdown,
    EditAutocomplete,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    segments: {
      type: Array as PropType<FilterSegment<unknown>[]>,
      required: true,
    },

    activeSegments: {
      type: Array as PropType<ActiveSegment[]>,
      required: true,
    },

    items: {
      type: Object,
      required: true,
    },

    segmentLabel: {
      type: String,
      required: false,
      default: 'automatedScenarios.fields.addSegment',
    },

    placeholderSelectSegments: {
      type: String,
      required: false,
      default: 'automatedScenarios.fields.segments.chooseSegment',
    },

    scenarioType: {
      type: String as PropType<TypeCampaignEnum>,
      required: false,
      default: TypeCampaignEnum.AUTOMATION,
    },

    currentStepId: {
      type: String,
      required: false,
      default: null,
    },

  },

  emits: ['add:segment', 'remove:segment'],

  setup(props: {
    segments: FilterSegment<unknown>[];
    activeSegments: ActiveSegment[];
    items: any;
    scenarioType: TypeCampaignEnum;
    currentStepId: string | null;
  }, { emit }: SetupContext) {
    const { t } = useI18n();
    const store = useStore();
    const formValidation = ref<any>({});
    const componentFieldErrorsKey = ref(0);

    // Get flow from store for dependency checking
    const activeFlow = computed(() => store.getters['flowEditor/getActiveFlow']);

    // Get insert context from parent (for new operators being added)
    const insertContext = inject<Ref<OperatorInsertContext | null>>('insertContext', ref(null));

    const flowContext = computed<FlowDependencyContext>(() => {
      const flow = activeFlow.value?.flowData ?? null;

      // If we have a currentStepId (edit mode), use it
      if (props.currentStepId) {
        return {
          flow,
          currentStepId: props.currentStepId,
        };
      }

      // For new operators, use insert context to get ancestor types
      if (insertContext?.value) {
        return {
          flow,
          currentStepId: null,
          directParentTypes: insertContext.value.parentOperatorId ? [insertContext.value.parentOperatorId] : [],
          allAncestorTypes: insertContext.value.allAncestorTypes ?? [],
        };
      }

      return {
        flow,
        currentStepId: null,
      };
    });

    const segments = toRef(props, 'segments');
    const selectedValue = ref('');
    const selectedSegment: Ref<FilterSegment<unknown>|undefined> = ref();
    const selectedSegmentNumber: Ref<number|null> = ref(null);
    const editableFields = computed(() => selectedSegment.value?.fields.filter((field: FilterField) => !field.hidden));

    // Component mapping for dynamic component resolution
    const componentMap: Record<string, any> = {
      ItemList,
      NumberRange,
      ButtonGroup,
      InputNumber,
      Input,
      Location,
      PeriodSelector,
      InputList,
      MultiDropdown,
      EditAutocomplete,
    };

    const resolveComponent = (componentName: string) => componentMap[componentName] || componentName;

    const newListId = ref();
    const showModal = ref(false);

    const options = computed(() => getAvailableSegmentsForFlow<unknown>(
      props.scenarioType,
      segments.value,
      props.activeSegments.map((activeSegment) => activeSegment.name),
      flowContext.value,
    )
      .filter((segment: FilterSegment<unknown>) => ((segment.multiple
        || !props.activeSegments.reduce((acc: string[], current: ActiveSegment) => ([...acc, current.name]), []).includes(segment.id))))
      .map((segment: FilterSegment<unknown>) => segment.option));

    const resetAll = () => {
      showModal.value = false;
      selectedSegment.value = undefined;
      selectedValue.value = '';
      selectedSegmentNumber.value = null;
    };

    const onModalHide = () => {
      resetAll();
    };

    const hasActiveSegment = () => props.activeSegments.length > 0;

    const handleRemoveSegment = (segmentName: string, number: number) => {
      emit('remove:segment', { segmentName, number });
    };

    const handleSaveSegment = async () => {
      formValidation.value = {};
      componentFieldErrorsKey.value += 1;
      if (selectedSegment?.value?.validate) {
        const data = selectedSegment.value?.fields.reduce((acc: {[key: number]: string}, cur: any) => {
          acc[cur.id] = cur.data;
          return acc;
        }, {});
        const validation = await selectedSegment.value.validate(data);
        if (!validation.success) {
          formValidation.value = validation.validate;
          componentFieldErrorsKey.value += 1;
          await showToastError(t('errorMessages.FORM_ERROR'));
          return;
        }
      }

      showModal.value = false;
      if (selectedSegmentNumber.value !== null && selectedSegment.value) {
        handleRemoveSegment(selectedSegment.value.id, selectedSegmentNumber.value);
      }
      if (selectedSegment.value) {
        emit('add:segment', selectedSegment.value);
      }
      selectedSegment.value = undefined;
      selectedValue.value = '';
      selectedSegmentNumber.value = null;
    };

    const loadSegment = (segment: string|any) => {
      formValidation.value = {};
      componentFieldErrorsKey.value += 1;

      selectedValue.value = typeof (segment) === 'string' ? segment : segment.value;

      selectedSegment.value = segments.value.filter((current: FilterSegment<unknown>) => current.option.value === selectedValue.value)[0];

      if (editableFields.value?.length === 0) {
        handleSaveSegment();
      } else {
        showModal.value = true;
      }
    };

    const editSegment = (segmentName: string, number?: number) => {
      selectedSegmentNumber.value = number !== undefined ? number : null;
      loadSegment(segmentName);

      selectedSegment.value?.fields.forEach((field: FilterField) => {
        field.data = props.items[segmentName][field.id];

        if (number !== undefined) {
          if (field.component === 'InputList') {
            field.data = props.items[segmentName][field.id];
          } else {
            field.data = props.items[segmentName][field.id][number];
          }
        }

        if (field.format) {
          field.data = field.format(field.data);
        }
      });
    };

    const showTooltip = (segmentName: string, number: number) => {
      const segment = segments.value.filter((current: FilterSegment<unknown>) => current.option.value === segmentName)[0];
      return tooltip(segment, props.items[segmentName], number);
    };

    const fetchLists = async () => {
      const { items, err } = await List<ShopsLists>({
        name: 'ShopsLists',
        settings: {
          filter: [
            {
              field: 'id_shop',
              operator: OperatorType.Equals,
              value: UserState.activeShop ? UserState.activeShop.id : 0,
            },
          ],
          order: [
            { field: 'id_shop_list', type: 'ASC' },
          ],
          limit: 0,
          offset: 0,
        },
        fields: ['id_shop_list', 'name'],
      });

      if (err === '') {
        return items.map((item) => ({
          name: item.name,
          code: item.id_shop_list,
        }));
      }

      return [];
    };

    watch(() => myListsState.refreshComponent, async () => {
      if (myListsState.refreshComponent !== 0 && selectedSegment.value && selectedSegment.value.id === 'filtermemberoflist') {
        newListId.value = myListsState.refreshComponent;
        const newLists = await fetchLists();

        const matchingCurrentLists = newLists.find((item: any) => item.code === asInt(newListId.value));
        if (matchingCurrentLists) {
          selectedSegment.value.fields[0].props.options = newLists.map((list) => ({ value: list.code, label: list.name }));
        }

        myListsState.refreshComponent = 0;
      }
    });

    return {
      t,
      formValidation,
      componentFieldErrorsKey,
      selectedValue,
      selectedSegment,
      options,
      editableFields,
      newListId,
      showModal,
      resolveComponent,

      showTooltip,
      loadSegment,
      editSegment,
      handleSaveSegment,
      handleRemoveSegment,
      hasActiveSegment,
      resetAll,
      onModalHide,
    };
  },

});
</script>

<style lang="scss" scoped>
.select-segment {
  width: 100%;
}

.segment-item {
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.p-segment-icon {
  width: 2rem !important;
  height: 2rem;
  &:focus {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }
}

.segment-config-content {
  padding: 0.5rem 0;
}
</style>

<style>
.segment-summary .p-tooltip-text {
  min-width: 20rem;
}

.segment-config-modal {
  .p-dialog-header {
    padding-bottom: 0.5rem;
  }
  
  .p-dialog-content {
    padding-top: 1rem;
    text-align: left;
  }

  .field-wrapper {
    text-align: left;
    
    label {
      text-align: left;
    }
  }
}
</style>
